import { useState, useEffect, useCallback, useRef } from 'react';
import type { Incident, SignalRMessage } from 'types';
import { SignalREventType } from 'types';
import { getActiveIncidents } from 'services';
import type { ConnectionStatus } from 'services';
import { useSignalR } from 'contexts';

export interface UseIncidentsOptions {
  onNewIncident?: (incident: Incident) => void;
}

export interface UseIncidentsResult {
  incidents: Incident[];
  closedLast24h: number;
  isLoading: boolean;
  error: string | null;
  connectionStatus: ConnectionStatus;
  refresh: () => Promise<void>;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

// Track closed incident timestamps (persisted across re-renders)
const closedTimestamps: number[] = [];

function cleanupOldClosed(): void {
  const cutoff = Date.now() - TWENTY_FOUR_HOURS_MS;
  while (closedTimestamps.length > 0 && closedTimestamps[0] < cutoff) {
    closedTimestamps.shift();
  }
}

export function useIncidents(options?: UseIncidentsOptions): UseIncidentsResult {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [closedLast24h, setClosedLast24h] = useState(closedTimestamps.length);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const onNewIncidentRef = useRef(options?.onNewIncident);

  // Get SignalR context - connection is managed at app level
  const { connectionStatus, subscribe } = useSignalR();

  // Keep callback ref up to date
  onNewIncidentRef.current = options?.onNewIncident;

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getActiveIncidents();
      if (mountedRef.current) {
        setIncidents(data);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load incidents');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Handle incoming SignalR messages
  const handleMessage = useCallback((message: SignalRMessage) => {
    // Track closed incidents
    if (message.eventType === SignalREventType.IncidentClosed) {
      closedTimestamps.push(Date.now());
      cleanupOldClosed();
      setClosedLast24h(closedTimestamps.length);
    }

    setIncidents((prev) => {
      switch (message.eventType) {
        case SignalREventType.IncidentCreated: {
          if (message.incident) {
            const exists = prev.some((i) => i.incidentId === message.incident!.incidentId);
            if (!exists) {
              // Trigger notification callback for new incidents
              onNewIncidentRef.current?.(message.incident);
              return [message.incident, ...prev];
            }
          }
          return prev;
        }

        case SignalREventType.IncidentUpdated:
        case SignalREventType.IncidentClosed: {
          const existingIndex = prev.findIndex((i) => i.incidentId === message.incidentId);

          if (existingIndex >= 0) {
            return prev.map((i) =>
              i.incidentId === message.incidentId
                ? { ...i, state: message.state, severity: message.severity, ...(message.incident ?? {}) }
                : i,
            );
          }

          if (message.incident) {
            return [message.incident, ...prev];
          }

          return prev;
        }

        default:
          return prev;
      }
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Load initial data
    refresh();

    // Subscribe to SignalR messages - connection is managed by SignalRProvider
    const unsubMessage = subscribe(handleMessage);

    // Periodically clean up old closed incidents (every minute)
    const cleanupInterval = setInterval(() => {
      cleanupOldClosed();
      setClosedLast24h(closedTimestamps.length);
    }, 60000);

    return () => {
      mountedRef.current = false;
      unsubMessage();
      clearInterval(cleanupInterval);
    };
  }, [refresh, handleMessage, subscribe]);

  return { incidents, closedLast24h, isLoading, error, connectionStatus, refresh };
}
